"use client";

import { useEffect, useState } from "react";

import { usePathname } from "next/navigation";

import { readCookie } from "@/utils/cookies";
import { segmentEvent } from "@/utils/segmentfunctions";

import { ExperimentConfig, EXPERIMENTS } from "./experiments.config";

interface ExperimentHandlerProps {
  experiments?: ExperimentConfig[];
}

/**
 * ExperimentHandler component that checks for specific cookies and triggers segment events
 * based on the current path and cookie values.
 *
 * This component should be injected in the ClientLayout.
 * Each experiment will only fire ONCE per session, no matter how often the user visits the page.
 */
export function ExperimentHandler({ experiments = EXPERIMENTS }: ExperimentHandlerProps): null {
  const pathname = usePathname();
  const [firedExperiments, setFiredExperiments] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Check each experiment configuration
    experiments.forEach((experiment) => {
      // Check if we're on the target path for this experiment
      if (pathname === experiment.path) {
        // Check if we've already fired this specific experiment in this session
        if (firedExperiments.has(experiment.id)) {
          return;
        }

        // Read the cookie for this experiment
        const cookieValue = readCookie(experiment.cookieName);

        // If cookie value matches the trigger value, fire the experiment
        if (cookieValue === experiment.triggerValue) {
          segmentEvent("Experiment Viewed", {
            experiment_id: experiment.experimentId,
            experiment_name: experiment.experimentName,
            variant_name: experiment.variantName,
          });

          // Mark that we've fired this specific experiment in this session
          setFiredExperiments((prev) => new Set(prev).add(experiment.id));
        }
      }
    });
  }, [pathname, experiments, firedExperiments]);

  return null;
}
